
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { dataService } from '../services/dataService';
import { CarRequest } from '../types';
import { Search, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';

export const RequestCar: React.FC = () => {
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<Omit<CarRequest, 'id' | 'status'>>();
  const [submitted, setSubmitted] = useState(false);

  const onSubmit = async (data: Omit<CarRequest, 'id' | 'status'>) => {
    try {
      // Data is sent to Firestore 'car_requests' AND 'mail' collections
      await dataService.submitCarRequest(data);
      
      reset();
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error("Submission Error:", err);
      alert("Submission failed. Please check your internet connection and try again.");
    }
  };

  const handleResetForm = () => {
    reset();
    setSubmitted(false);
  };

  if (submitted) {
    return (
      <div className="bg-dark min-h-screen py-24 px-4 flex items-center justify-center">
        <div className="max-w-md w-full bg-card rounded-3xl border border-primary/20 p-12 text-center shadow-2xl animate-fade-in">
          <div className="bg-primary/10 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-8">
            <CheckCircle2 size={48} className="text-primary" />
          </div>
          <h2 className="text-3xl font-heading font-bold text-white mb-4 uppercase tracking-tight">Request Logged</h2>
          <p className="text-gray-400 mb-10 leading-relaxed">
            Your vehicle sourcing request has been successfully recorded. 
            A dispatch notification has been queued for <span className="text-white font-bold">info@dakarautosales.com</span>.
          </p>
          <button 
            onClick={handleResetForm}
            className="w-full bg-primary hover:bg-white hover:text-primary text-white font-heading font-bold py-4 rounded-xl uppercase tracking-widest transition-all shadow-xl shadow-primary/20"
          >
            Submit Another Request
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-dark min-h-screen py-16 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
            <Search className="text-primary" size={28} />
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4 uppercase tracking-tight font-heading">Request a <span className="text-primary">Specific Car</span></h1>
          <p className="text-gray-400 text-lg font-light max-w-xl mx-auto">
            Tell us exactly what you're looking for. We hunt down premium units through our global sourcing network.
          </p>
        </div>

        {Object.keys(errors).length > 0 && (
          <div className="mb-6 bg-danger/10 border border-danger/20 rounded-xl p-4 flex items-center gap-3 text-danger text-xs font-bold uppercase tracking-widest">
            <AlertCircle size={18} />
            Required fields are missing.
          </div>
        )}

        <div className="bg-card rounded-2xl shadow-xl border border-gray-800 p-8 md:p-12 relative">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            <div>
              <div className="flex items-center gap-3 mb-8">
                <div className="h-6 w-1 bg-primary"></div>
                <h3 className="text-sm font-bold text-white uppercase tracking-widest">Client Credentials</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 mb-2">Full Name</label>
                  <input 
                    {...register("name", { required: true })}
                    className={`w-full bg-darker border rounded-lg px-5 py-4 text-white focus:ring-1 focus:ring-primary outline-none transition-all ${errors.name ? 'border-danger' : 'border-gray-700'}`}
                    placeholder="Jane Doe"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 mb-2">Phone Number</label>
                  <input 
                    {...register("phone", { required: true })}
                    className={`w-full bg-darker border rounded-lg px-5 py-4 text-white focus:ring-1 focus:ring-primary outline-none transition-all ${errors.phone ? 'border-danger' : 'border-gray-700'}`}
                    placeholder="(773) 863-XXXX"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 mb-2">Email Address</label>
                  <input 
                    {...register("email", { 
                      required: true, 
                      pattern: /^\S+@\S+$/i 
                    })}
                    className={`w-full bg-darker border rounded-lg px-5 py-4 text-white focus:ring-1 focus:ring-primary outline-none transition-all ${errors.email ? 'border-danger' : 'border-gray-700'}`}
                    placeholder="jane@example.com"
                  />
                </div>
              </div>
            </div>

            <div className="pt-8 border-t border-gray-800">
              <div className="flex items-center gap-3 mb-8">
                <div className="h-6 w-1 bg-accent"></div>
                <h3 className="text-sm font-bold text-white uppercase tracking-widest">Vehicle Specifications</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 mb-2">Manufacturer</label>
                  <input 
                    {...register("make", { required: true })}
                    className={`w-full bg-darker border rounded-lg px-5 py-4 text-white focus:ring-1 focus:ring-primary outline-none ${errors.make ? 'border-danger' : 'border-gray-700'}`}
                    placeholder="e.g. BMW"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 mb-2">Model</label>
                  <input 
                    {...register("model", { required: true })}
                    className={`w-full bg-darker border rounded-lg px-5 py-4 text-white focus:ring-1 focus:ring-primary outline-none ${errors.model ? 'border-danger' : 'border-gray-700'}`}
                    placeholder="e.g. X5"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-gray-400 mb-2">Year Range</label>
                  <input 
                    {...register("yearRange", { required: true })}
                    className={`w-full bg-darker border rounded-lg px-5 py-4 text-white focus:ring-1 focus:ring-primary outline-none ${errors.yearRange ? 'border-danger' : 'border-gray-700'}`}
                    placeholder="e.g. 2020-2023"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-gray-400 mb-2">Budget Range</label>
                  <input 
                    {...register("priceRange", { required: true })}
                    className={`w-full bg-darker border rounded-lg px-5 py-4 text-white focus:ring-1 focus:ring-primary outline-none ${errors.priceRange ? 'border-danger' : 'border-gray-700'}`}
                    placeholder="e.g. $40,000"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-gray-400 mb-2">Special Requirements</label>
                  <textarea 
                    {...register("message")}
                    rows={4}
                    className="w-full bg-darker border border-gray-700 rounded-lg px-5 py-4 text-white focus:ring-1 focus:ring-primary outline-none resize-none"
                    placeholder="Color, interior, features..."
                  ></textarea>
                </div>
              </div>
            </div>

            <div className="pt-6">
              <button 
                disabled={isSubmitting}
                type="submit"
                className="w-full bg-primary hover:bg-white hover:text-primary text-white font-heading font-bold py-5 rounded-xl shadow-2xl shadow-primary/20 transition-all uppercase tracking-[0.3em] flex items-center justify-center gap-3 disabled:opacity-50"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="animate-spin" size={20} />
                    Processing...
                  </>
                ) : 'Submit Sourcing Request'}
              </button>
              <p className="text-center text-[9px] text-gray-600 uppercase font-bold tracking-widest mt-6">
                Relaying to mail.dakarautosales.com:465
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
